'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const STORAGE_KEY = 'preferredLang';

// All available languages
const ALL_LANGUAGES = [
  { code: 'en', flag: '🇬🇧', label: 'English', ariaLabel: 'English' },
  {
    code: 'zh',
    flag: '🇨🇳',
    label: '简体中文',
    ariaLabel: 'Simplified Chinese',
  },
  { code: 'ar', flag: '🇸🇦', label: 'العربية', ariaLabel: 'Arabic' },
] as const;

// Get enabled languages from environment variable
function getEnabledLanguages() {
  const envLanguages = process.env.NEXT_PUBLIC_LANGUAGES || 'en,ar';

  if (!envLanguages) {
    // If no environment variable is set, show all languages
    return ALL_LANGUAGES;
  }

  const enabledCodes = envLanguages.split(',').map(code => code.trim());
  return ALL_LANGUAGES.filter(lang => enabledCodes.includes(lang.code));
}

export default function LanguageSelectorModal() {
  const { i18n } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const enabledLanguages = getEnabledLanguages();

  useEffect(() => {
    try {
      const saved =
        typeof window !== 'undefined'
          ? localStorage.getItem(STORAGE_KEY)
          : null;
      if (saved) {
        if (i18n.language !== saved) {
          i18n.changeLanguage(saved);
        }
        setOpen(false);
      } else {
        // No saved preference -> show modal
        setOpen(true);
      }
    } catch {
      // If localStorage unavailable, just keep default and hide modal
      setOpen(false);
    }
  }, [i18n]);

  const choose = (lang: string) => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {}
    i18n.changeLanguage(lang);
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div style={overlayStyle}>
      <div
        style={modalStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lang-title"
      >
        <h3 id="lang-title" style={titleStyle}>
          Choose your language
        </h3>
        <div style={optionsStyle}>
          {enabledLanguages.map(lang => (
            <button
              key={lang.code}
              type="button"
              onClick={() => choose(lang.code)}
              style={optionButtonStyle}
              aria-label={lang.ariaLabel}
            >
              <span style={flagStyle} aria-hidden>
                {lang.flag}
              </span>
              <span style={labelStyle}>{lang.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
};

const modalStyle: React.CSSProperties = {
  background: 'white',
  borderRadius: 16,
  padding: 24,
  width: '90%',
  maxWidth: 420,
  boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
  textAlign: 'center',
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  marginBottom: 16,
  color: '#333',
  fontSize: 20,
  fontWeight: 700,
};

const optionsStyle: React.CSSProperties = {
  display: 'flex',
  gap: 16,
  justifyContent: 'center',
};

const optionButtonStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
  padding: 12,
  borderRadius: 12,
  border: '1px solid #e5e7eb',
  background: 'white',
  cursor: 'pointer',
  minWidth: 120,
};

const flagStyle: React.CSSProperties = {
  fontSize: 36,
  lineHeight: 1,
};

const labelStyle: React.CSSProperties = {
  color: '#111827',
  fontSize: 14,
  fontWeight: 600,
};
